import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, useSEO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const field = "w-full bg-[#F5F5F5] rounded-full px-6 py-4 text-sm outline-none focus:ring-1 ring-black transition";
const btn = "w-full bg-black text-white rounded-full py-4 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors disabled:opacity-60";

const Shell = ({ title, sub, children, testid }) => (
  <div data-testid={testid} className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
    <div className="w-full max-w-md">
      <Link to="/" className="font-display text-3xl tracking-[0.35em] uppercase block text-center mb-10">VYRON</Link>
      <h1 className="font-display text-4xl tracking-tight text-center mb-2">{title}</h1>
      <p className="text-sm text-muted-foreground text-center mb-8">{sub}</p>
      {children}
    </div>
  </div>
);

export function Login() {
  useSEO("Sign In");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(f.email, f.password);
      toast.success("Welcome back.");
      navigate("/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Sign in" sub="Welcome back to VYRON." testid="login-page">
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <input data-testid="login-email" type="email" required placeholder="Email" value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })} className={field} />
        <input data-testid="login-password" type="password" required placeholder="Password" value={f.password}
          onChange={(e) => setF({ ...f, password: e.target.value })} className={field} />
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-xs text-muted-foreground link-underline">Forgot password?</Link>
        </div>
        <button type="submit" disabled={busy} data-testid="login-submit" className={btn}>{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      <p className="text-sm text-center text-muted-foreground mt-6">
        New to VYRON? <Link to="/register" data-testid="to-register" className="text-black link-underline">Create account</Link>
      </p>
    </Shell>
  );
}

export function Register() {
  useSEO("Create Account");
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(f.name, f.email, f.password);
      toast.success("Account created. Welcome to VYRON.");
      navigate("/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Create account" sub="Join the VYRON world." testid="register-page">
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        <input data-testid="register-name" required placeholder="Full name" value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })} className={field} />
        <input data-testid="register-email" type="email" required placeholder="Email" value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })} className={field} />
        <input data-testid="register-password" type="password" required minLength={6} placeholder="Password (min 6 chars)" value={f.password}
          onChange={(e) => setF({ ...f, password: e.target.value })} className={field} />
        <button type="submit" disabled={busy} data-testid="register-submit" className={btn}>{busy ? "Creating…" : "Create account"}</button>
      </form>
      <p className="text-sm text-center text-muted-foreground mt-6">
        Already have an account? <Link to="/login" data-testid="to-login" className="text-black link-underline">Sign in</Link>
      </p>
    </Shell>
  );
}

export function ForgotPassword() {
  useSEO("Reset Password");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      if (data.demo_reset_link) setLink(data.demo_reset_link);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Reset password" sub="We'll send you a reset link." testid="forgot-page">
      <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
        <input data-testid="forgot-email" type="email" required placeholder="Email" value={email}
          onChange={(e) => setEmail(e.target.value)} className={field} />
        <button type="submit" disabled={busy} data-testid="forgot-submit" className={btn}>{busy ? "Sending…" : "Send reset link"}</button>
      </form>
      {link && (
        <div className="mt-6 bg-[#F5F5F5] rounded-2xl p-4 text-xs" data-testid="demo-reset-link">
          <p className="text-muted-foreground mb-2">Demo mode (no email service configured yet). Use this link:</p>
          <Link to={link.substring(link.indexOf("/reset-password"))} className="text-black underline break-all">{link}</Link>
        </div>
      )}
      <p className="text-sm text-center text-muted-foreground mt-6">
        <Link to="/login" className="text-black link-underline">Back to sign in</Link>
      </p>
    </Shell>
  );
}

export function ResetPassword() {
  useSEO("Set New Password");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message);
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Shell title="New password" sub="Choose a new password." testid="reset-page">
      {!token ? (
        <p className="text-sm text-center text-destructive">Invalid or missing reset token.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
          <input data-testid="reset-password" type="password" required minLength={6} placeholder="New password" value={password}
            onChange={(e) => setPassword(e.target.value)} className={field} />
          <button type="submit" disabled={busy} data-testid="reset-submit" className={btn}>{busy ? "Updating…" : "Update password"}</button>
        </form>
      )}
    </Shell>
  );
}
